import apiClient from './api';

export interface FeatureFlag {
  id: string;
  name: string; // Display name
  key: string; // Technical key (lowercase, numbers, underscores)
  description?: string;
  category?: string;
  isActive: boolean;
  companyId: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateFeatureFlagRequest {
  name: string;
  key: string;
  description?: string;
  category?: string;
}

export interface UpdateFeatureFlagRequest {
  name?: string;
  key?: string;
  description?: string;
  category?: string;
  isActive?: boolean;
}

export interface ToolMapping {
  serverId: string;
  serverName: string;
  tools: Array<{
    toolId: string;
    toolName: string;
    toolDescription?: string;
  }>;
}

export interface ToolPreviewResult {
  serverIds: string[];
  featureFlags?: string[];
  toolCount: number;
  tools: Array<{
    id: number;
    name: string;
    description: string;
    inputSchema: any;
  }>;
}

export interface ComparisonResult {
  scenarioA: {
    serverIds: string[];
    featureFlags?: string[];
    toolCount: number;
    toolNames: string[];
  };
  scenarioB: {
    serverIds: string[];
    featureFlags?: string[];
    toolCount: number;
    toolNames: string[];
  };
  comparison: {
    onlyInA: string[];
    onlyInB: string[];
    inBoth: string[];
  };
}

export const featureFlagService = {
  // Feature Flag CRUD
  getAllFlags: async (): Promise<FeatureFlag[]> => {
    const response = await apiClient.get('/FeatureFlag');
    return response.data;
  },

  getActiveFlags: async (): Promise<FeatureFlag[]> => {
    const response = await apiClient.get('/FeatureFlag/active');
    return response.data;
  },

  getFlagById: async (id: string): Promise<FeatureFlag> => {
    const response = await apiClient.get(`/FeatureFlag/${id}`);
    return response.data;
  },

  getFlagByKey: async (key: string): Promise<FeatureFlag> => {
    const response = await apiClient.get(`/FeatureFlag/key/${key}`);
    return response.data;
  },

  createFlag: async (data: CreateFeatureFlagRequest): Promise<FeatureFlag> => {
    const response = await apiClient.post('/FeatureFlag', data);
    return response.data;
  },

  updateFlag: async (id: string, data: UpdateFeatureFlagRequest): Promise<FeatureFlag> => {
    const response = await apiClient.put(`/FeatureFlag/${id}`, data);
    return response.data;
  },

  deleteFlag: async (id: string): Promise<void> => {
    await apiClient.delete(`/FeatureFlag/${id}`);
  },

  // Tool Mapping Operations
  assignToolsToFlag: async (flagKey: string, serverId: string, toolIds: string[]): Promise<void> => {
    await apiClient.post('/ToolFeatureFlagMapping/assign', {
      featureFlagKey: flagKey,
      serverId,
      toolIds
    });
  },

  removeToolsFromFlag: async (flagKey: string, serverId: string, toolIds: string[]): Promise<void> => {
    await apiClient.post('/ToolFeatureFlagMapping/remove', {
      featureFlagKey: flagKey,
      serverId,
      toolIds
    });
  },

  getMappingsByFlag: async (flagKey: string): Promise<ToolMapping[]> => {
    const response = await apiClient.get(`/ToolFeatureFlagMapping/flag/${flagKey}`);
    return response.data;
  },

  getFlagsForTool: async (toolId: string, serverId: string): Promise<{ toolId: string; serverId: string; flags: string[] }> => {
    const response = await apiClient.get(`/ToolFeatureFlagMapping/tool/${toolId}/server/${serverId}`);
    return response.data;
  },

  // Tool Preview
  previewTools: async (serverIds: string[], featureFlags?: string[]): Promise<ToolPreviewResult> => {
    const response = await apiClient.post('/ToolPreview', {
      serverIds,
      featureFlags
    });
    return response.data;
  },

  compareToolsets: async (
    scenarioA: { serverIds: string[]; featureFlags?: string[] },
    scenarioB: { serverIds: string[]; featureFlags?: string[] }
  ): Promise<ComparisonResult> => {
    const response = await apiClient.post('/ToolPreview/compare', {
      scenarioA,
      scenarioB
    });
    return response.data;
  }
};
